import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaSun, FaLeaf, FaCheck, FaClock, FaUsers, FaShieldAlt, FaLightbulb, FaHandsHelping } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import irTherapyImage from '../../components/assets/Staff/IR Therapy 2.jpg';
import irTherapy1Image from '../../components/assets/Staff/IR Therapy 1.jpg';
import irTherapy3Image from '../../components/assets/Staff/IR Therapy 3.jpg';
import irTherapy4Image from '../../components/assets/Staff/IR Therapy 4.jpg';

const RedLightSessions = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const sessionRef = useRef(null);
  const benefitsRef = useRef(null);
  const complementRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'session': sessionRef,
      'benefits': benefitsRef,
      'complement': complementRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'session', ref: sessionRef },
        { id: 'benefits', ref: benefitsRef },
        { id: 'complement', ref: complementRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'session', label: 'How Light Supports', icon: FaSun },
    { id: 'benefits', label: 'What This May Support', icon: FaLeaf },
    { id: 'complement', label: 'Designed for Modern Lives', icon: FaHandsHelping }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Non-Invasive",
      description: "No heat, no pressure, no effort – you simply rest while light does the rest"
    },
    {
      icon: FaSun,
      title: "Red & Near-Infrared",
      description: "660 nm works closer to the surface; 850 nm reaches deeper layers"
    },
    {
      icon: FaLeaf,
      title: "Natural Rhythm",
      description: "Supports circulation, cellular activity, and the body's natural regulatory processes"
    }
  ];

  const lightBenefits = [
    "Red light (660 nm) works closer to the surface",
    "Near-infrared light (850 nm) reaches deeper layers of the body",
    "Widely used in wellness environments to support circulation and cellular activity",
    "Supports the body's natural regulatory processes",
    "No heat, no pressure, no effort required – you simply rest"
  ];

  const sessionBenefits = [
    "Physical ease and muscle relaxation, particularly after long periods of sitting or activity",
    "Post-activity recovery and comfort that supports an active lifestyle",
    "A calmer nervous system, with reduced feelings of overload or tension",
    "Improved mental clarity and focus during demanding periods",
    "Deeper rest and improved sleep quality",
    "Healthier daily rhythms",
    "Skin that appears fresher, softer, and more radiant",
    "A general sense of balance, grounding, and renewed energy"
  ];

  const complementPractices = [
    "Fast-paced schedules, high mental load, or frequent travel",
    "Desk-based work or regular physical training",
    "Stretching, breathwork, meditation, massage, or mindful movement",
    "A gentle start to the day or a way to fully unwind in the evening",
    "Something increasingly rare: uninterrupted time to slow down"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Red Light Sessions with Helios" description="Red light and near-infrared therapy with Helios at Bioresonance Leicester. Gentle, non-invasive support for recovery and comfort. Book at 108 New Walk." path="/therapy/red-light-sessions" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Red Light Sessions</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaSun className="mr-2" />
                A deeply calming light experience supporting balance, recovery, and everyday vitality
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Red Light Sessions with Helios
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Light doesn't rush, doesn't force change, and doesn't demand results. It creates space.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Helios red and near-infrared light sessions offer a full-body, non-invasive wellness experience designed to support your body's natural rhythm, helping you feel more at ease, more present, and more connected to yourself. This experience is not about fixing the body, but about supporting what already knows how to respond.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>30-60 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container - same photo as Red Light card */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={irTherapyImage}
                      alt="Red Light Sessions with Helios"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What Makes Helios Special
              </h2>
              <p className="text-lg text-gray-600">
                Full-body, non-invasive light experience – no heat, no pressure, no effort. You simply rest while light supports circulation, cellular activity, and the body's natural regulatory processes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Awareness Disclaimer */}
            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaLightbulb className="text-xl text-gold" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    Not about fixing – about supporting
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    There is no heat, no pressure, and no effort required. You simply rest while light does the rest. 
                    This experience is not about fixing the body, but about supporting what already knows how to respond.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Light Supports Section */}
      <section ref={sessionRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  How this light experience supports the body
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    Helios combines red light (660 nm), which works closer to the surface, with near-infrared light (850 nm), which reaches deeper layers of the body. These wavelengths are widely used in wellness environments to support circulation, cellular activity, and the body's natural regulatory processes.
                  </p>
                  <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                    <p className="text-dark-grey font-semibold text-lg">
                      There is no heat, no pressure, and no effort required. You simply rest while light does the rest.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl h-80">
                    <img
                      src={irTherapy1Image}
                      alt="Light therapy technology"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2"></div>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-2xl font-bold text-dark-grey mb-6 text-center">Red & near-infrared light</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {lightBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{benefit}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="relative">
                  <div className="relative group">
                    <div className="relative overflow-hidden rounded-3xl h-96">
                      <img
                        src={irTherapy3Image}
                        alt="Light wavelengths"
                        className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You May Notice */}
      <section ref={benefitsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What this experience may support
              </h2>
              <p className="text-lg text-gray-600">
                While everyone responds differently, people who return regularly often describe support in areas such as:
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="space-y-4 mb-6">
                  {sessionBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{benefit}</p>
                    </motion.div>
                  ))}
                </div>
                <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                  <p className="text-dark-grey font-semibold text-lg">
                    Some people notice subtle shifts after the first session, while for others the experience unfolds gradually over time. There are no expectations, only an opportunity to notice how your body responds.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark-grey mb-4">Your experience</h3>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    Many notice a calmer nervous system, with reduced feelings of overload or tension, alongside improved mental clarity and focus during demanding periods.
                  </p>
                  <p>
                    The experience may also support deeper rest and improved sleep quality, healthier daily rhythms, and skin that appears fresher, softer, and more radiant – as well as a general sense of balance, grounding, and renewed energy.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Natural Complement Section */}
      <section ref={complementRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHandsHelping className="mr-2" />
                Designed for modern, busy lives
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Designed for modern, busy lives
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Helios sessions are especially valued by people navigating fast-paced schedules, high mental load, frequent travel, desk-based work, or regular physical training.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  They integrate naturally into a wellness-focused lifestyle and complement practices such as stretching, breathwork, meditation, massage, or mindful movement. Whether used as a gentle start to the day or a way to fully unwind in the evening, each session offers something increasingly rare: uninterrupted time to slow down.
                </p>
                <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                  <h3 className="text-xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>Full-body immersion, not targeted exposure</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Helios is designed for complete, full-body light immersion, creating a cocoon-like environment rather than focusing on a single area. The experience is private, unhurried, and deeply calming, allowing you to fully relax, breathe, and rest in a space that supports balance and stillness.
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl h-80">
                    <img
                      src={irTherapy4Image}
                      alt="Full-body light immersion"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2"></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4">Who values Helios</h3>
                <div className="space-y-3">
                  {complementPractices.map((practice, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-xs" />
                      </div>
                      <p className="text-gray-600 text-sm">{practice}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4">A space to slow down</h3>
                <p className="text-gray-600 leading-relaxed">
                  Each session offers something increasingly rare: uninterrupted time to slow down. The experience is private, unhurried, and deeply calming – whether as a gentle start to the day or a way to fully unwind in the evening.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Experience Red Light Sessions with Helios?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            A deeply calming light experience – balance, recovery, and everyday vitality. Uninterrupted time to slow down and let your body respond in its own way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default RedLightSessions;
